// Browser adapter of the native-device gateway (BL-2090): a plain web page has no
// phone-app capabilities, so it keeps today's web behaviour — the one anchor
// download admin-frontend has (BL-2064), no platform auth session, no offline store and no
// media staging.
import type { NativeTransferQueue } from '../native-transfer/types';
import type { OfflineMediaStaging, OfflineScope, OfflineStore } from '../offline-store/types';
import {
  NativeCapabilityUnavailableError,
  type AuthSessionOutcome,
  type NativeDeviceGateway,
  type SaveFileInput,
} from '../gateway';

/** The gateway in a desktop or phone browser: today's web behaviour everywhere. */
export class BrowserNativeDeviceGateway implements NativeDeviceGateway {
  /** @returns false: a browser is never the native app. */
  isNativeApp(): boolean {
    return false;
  }

  /**
   * @returns never; a browser has no platform auth session.
   * @throws NativeCapabilityUnavailableError always, so callers take the web path.
   */
  async openAuthSession(): Promise<AuthSessionOutcome> {
    throw new NativeCapabilityUnavailableError('openAuthSession');
  }

  /**
   * Offline mode is the phone app's alone (ADR 066 — Offline Mode § 1): a desktop browser
   * keeps no device store, and nothing touches IndexedDB.
   *
   * @returns never.
   * @throws NativeCapabilityUnavailableError('offlineStore') always.
   */
  async openOfflineStore(): Promise<OfflineStore> {
    throw new NativeCapabilityUnavailableError('offlineStore');
  }

  /**
   * A desktop browser stages nothing (BL-2421): offline photos are the phone app's alone.
   *
   * @returns never.
   * @throws NativeCapabilityUnavailableError('mediaStaging') always.
   */
  async openMediaStaging(): Promise<OfflineMediaStaging> {
    throw new NativeCapabilityUnavailableError('mediaStaging');
  }

  /**
   * A desktop browser has no native transfer queue (BL-2678).
   *
   * @returns never.
   * @throws NativeCapabilityUnavailableError('nativeTransfer') always.
   */
  async openNativeTransfer(): Promise<NativeTransferQueue> {
    throw new NativeCapabilityUnavailableError('nativeTransfer');
  }

  /**
   * @returns never: a browser keeps no device store.
   * @throws NativeCapabilityUnavailableError('offlineStore') always.
   */
  async readActiveOfflineScope(): Promise<OfflineScope | null> {
    throw new NativeCapabilityUnavailableError('offlineStore');
  }

  /**
   * @returns never: a browser keeps no device store, and writes nothing.
   * @throws NativeCapabilityUnavailableError('offlineStore') always.
   */
  async setActiveOfflineScope(): Promise<void> {
    throw new NativeCapabilityUnavailableError('offlineStore');
  }

  /**
   * Download the file through a temporary `<a download>`.
   *
   * @param input - the file and the name the browser saves it under.
   * @returns once the download has started.
   * @throws when the browser refuses the click.
   * @sideEffect Starts a browser download.
   */
  async saveFile({ blob, filename }: SaveFileInput): Promise<void> {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    // Firefox ignores a click on an anchor that isn't in the document.
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    }
  }

  /**
   * A browser is never opened from an app link.
   *
   * @returns a no-op unsubscribe.
   */
  onAppLinkOpen(): () => void {
    return () => {};
  }
}
